// useTableStarter — the summary the Tables helper opens with.
//
// While the helper is open on a saved table, the table's analysis is fetched
// from goapi's POST /chat/table/analysis/starter: one cached analysis per
// table, shared by everyone (a filtered table is analysed each time and not
// stored). It is drawn as the pane's first row and added to the page context,
// so the agent builds on it instead of repeating it. No agent chat is made for
// it.
//
// - One row per table, made for the filter in force when it was first shown,
//   oldest first. A filter applied after it asks nothing more: the chat reports
//   what changed. Going back to a table already shown asks nothing.
// - Closing keeps the rows, so reopening on the same table shows them again
//   (the chat is kept too, usePageAgent resumeKey). Reopened on another table,
//   they go.
// - The table's markdown is sent only once the data on screen is the table and
//   filter now open: data loaded before a switch is never posted for the next.
// - A refusal (AI access, budget) or a failure is a row too, in words.

import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { getTableMarkdown } from '../utils/tableMarkdown'
import { extractErrorMessage } from '../utils/apitools'
import type { AgentMessage } from '../agent/types'

/** The starter request waits this long for the page to settle (a quick filter change, a table switch). */
export const STARTER_DEBOUNCE_MS = 300
/** The most of the summary the page context carries. */
export const MAX_STARTER_CONTEXT = 2500

export const starterFailure = (reason: string) => `The summary could not be made: ${reason}`

/** The heading the summary row is drawn under (Adam, 19 Oct: "a title at the top"). */
export const STARTER_TITLE = 'Table summary'
const titled = (r: { filter: string; message: AgentMessage }): AgentMessage => ({
  ...r.message,
  content: `**${STARTER_TITLE}${r.filter ? ` (filter ${r.filter})` : ''}**\n\n${r.message.content}`,
})

interface UseTableStarterProps {
  customer: string
  job: string
  /** The open table's path (with or without .cbt). */
  path: string
  /** The quick filter in force, '' for none. */
  filter: string
  /** The table on screen, as loaded. */
  tableData: PlatinumData | undefined
  /** The helper is open on a saved table. Off: nothing is asked; the rows stay for a reopen on the same table. */
  enabled: boolean
  /** Send the side axis' question wording with the description (the job's include_side_label). */
  includeSideLabel?: boolean
}

interface StarterResponse {
  analysis: string
  cached: boolean
  filtered?: string
}

interface StarterRow {
  key: string
  path: string
  filter: string
  ok: boolean
  message: AgentMessage
}

const cbtPath = (path: string) => path.replace(/\.cbt$/i, '') + '.cbt'
const rowKey = (path: string, filter: string) => `${cbtPath(path)}\n${filter}`

export default function useTableStarter({ customer, job, path, filter, tableData, enabled, includeSideLabel = true }: UseTableStarterProps) {
  const [rows, setRows] = useState<StarterRow[]>([])
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const inFlightRef = useRef<{ key: string; controller: AbortController } | null>(null)
  // The table the helper was closed on; reopened on another, the rows go.
  const closedOnRef = useRef<string | null>(null)

  const key = rowKey(path, filter)

  // Which table and filter the data on screen was loaded for: the key when its
  // identity last changed. A switch keeps the old data until the new loads, and
  // that old data must not be posted under the new key.
  const [loaded, setLoaded] = useState<{ data: PlatinumData | undefined; key: string }>({ data: tableData, key })
  if (loaded.data !== tableData) setLoaded({ data: tableData, key })
  const dataIsCurrent = loaded.data === tableData ? loaded.key === key : true

  const latest = { customer, job, path, filter, tableData, includeSideLabel, rows }
  const latestRef = useRef(latest)
  latestRef.current = latest

  const abortInFlight = () => {
    inFlightRef.current?.controller.abort()
    inFlightRef.current = null
    setPendingKey(null)
  }

  const fetchStarter = async (forKey: string, controller: AbortController) => {
    const { customer: c, job: j, path: p, filter: f, tableData: data, includeSideLabel: side } = latestRef.current
    const markdown = getTableMarkdown(data)
    if (!markdown) return
    const sideLabel = side ? data?.meta?.side : undefined
    const description = [
      data?.properties?.description,
      sideLabel ? `Side axis (question wording): ${sideLabel}` : '',
    ].filter(Boolean).join('. ')
    const startedAt = new Date().toISOString()
    let ok = true
    let content: string
    try {
      const res = await axios.post<StarterResponse>(
        `${API_BASE_URL}/chat/table/analysis/starter`,
        { customer: c, job: j, path: cbtPath(p), description, markdown, filter: f },
        { signal: controller.signal },
      )
      content = res.data?.analysis ?? ''
      if (!content) {
        ok = false
        content = starterFailure('the reply was empty.')
      }
    } catch (error) {
      if (controller.signal.aborted) return
      ok = false
      content = starterFailure(extractErrorMessage(error) || 'unknown error.')
    }
    if (controller.signal.aborted) return
    inFlightRef.current = null
    setPendingKey(null)
    setRows((prev) => (prev.some((r) => r.key === forKey) ? prev : [
      ...prev,
      { key: forKey, path: cbtPath(p), filter: f, ok, message: { id: `starter:${forKey}`, role: 'assistant', content, timestamp: startedAt } },
    ]))
  }

  // Trigger: the helper opening or closing, another table or filter, and the
  // table's data arriving. Asks once per table and filter, after a short wait.
  useEffect(() => {
    const table = `${customer}/${job}/${cbtPath(path)}`
    if (!enabled) {
      if (inFlightRef.current) abortInFlight()
      if (closedOnRef.current === null) closedOnRef.current = table
      return
    }
    let shown = latestRef.current.rows
    if (closedOnRef.current !== null) {
      if (closedOnRef.current !== table) {
        shown = []
        setRows((prev) => (prev.length ? [] : prev))
      }
      closedOnRef.current = null
    }
    // Another table or filter: what is still on its way is for the old one.
    if (inFlightRef.current && inFlightRef.current.key !== key) abortInFlight()
    if (!dataIsCurrent || !getTableMarkdown(tableData)) return
    if (inFlightRef.current?.key === key) return
    if (shown.some((r) => r.path === cbtPath(path))) return
    const timer = setTimeout(() => {
      const controller = new AbortController()
      inFlightRef.current = { key, controller }
      setPendingKey(key)
      void fetchStarter(key, controller)
    }, STARTER_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, tableData, dataIsCurrent, customer, job])

  // Lifecycle: a request still on its way when the page goes is dropped.
  useEffect(() => () => { inFlightRef.current?.controller.abort() }, [])

  /** What the agent is told was already shown for the table open now, and the filter it was made for. */
  const contextBlock = (): string => {
    const { path: p, rows: shown } = latestRef.current
    const row = shown.find((r) => r.path === cbtPath(p) && r.ok)
    if (!row) return ''
    const block = `Summary already shown to the person for ${row.path} (${row.filter ? `filter ${row.filter}` : 'no filter'}):\n${row.message.content}`
    return block.length > MAX_STARTER_CONTEXT ? block.slice(0, MAX_STARTER_CONTEXT) : block
  }

  return {
    rows: rows.map(titled),
    // From the moment the table's data is current until its row is in, the debounce
    // included: a person who sends in that gap races the summary (#1908).
    loading: enabled && (pendingKey !== null || (dataIsCurrent && !!getTableMarkdown(tableData) && !rows.some((r) => r.path === cbtPath(path)))),
    contextBlock,
  }
}
